import { useEffect, useMemo, useState } from "react";
import { useGetAllGroups, useGetMyGroups, useRequestGroup } from "../../../api/GroupApi";
import { Box, Button, HStack, Image, Td, Text } from "@chakra-ui/react";
import { assetIcons } from "../../../constants/icons";
import { StyledTable } from "../../../components/StyledTable/StyledTable";
import { Pagination } from "../../../components/Pagination/Pagination";
import { Group } from "../../../types/Group";
import { GroupCodeYellowStats } from "../../../types/GroupCodeYellowStats";

export const AllGroupsTable = () => {
  const { mutate: requestGroup, isPending: isRequestingGroup } = useRequestGroup();
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const { data: groupsData, isLoading: isGettingAllGroups } = useGetAllGroups(
    page,
    perPage,
  );
  const { data: myGroupsData, isLoading: isMyGroupLoading } = useGetMyGroups(1, 0);

  useEffect(() => {
    if (groupsData?.currentPage) {
      setPage(groupsData?.currentPage);
    }
  }, [groupsData?.currentPage]);

  const tableColumns = [
    { key: "facility", label: "Facility" },
    { key: "providers", label: "Providers" },
    { key: "phone", label: "Phone Number" },
    { key: "description", label: "Description" },
    { key: "usdPerRvuHigh", label: "Highest $/RVU" },
    { key: "usdPerRvuLow", label: "Lowest $/RVU" },
    { key: "usdPerRvuRecent", label: "Recent $/RVU" },
    { key: "actions", label: "" },
  ];

  const allGroupsData = useMemo(
    () =>
      groupsData?.docs?.map((group) => {
        return {
          ...group,
          facility: group.facilityName,
          providers: group?.groupProviders?.length,
        };
      }),
    [groupsData?.docs],
  );

  const groupRow = (row: Group & GroupCodeYellowStats) => {
    return (
      <>
        <Td>
          <Text textStyle={"smMdSemi"}>{row.facilityName}</Text>
        </Td>
        <Td>
          <HStack>
            <Image src={assetIcons.userOutline} />
            <Text textStyle={"smBold"}>{row?.groupProviders?.length}</Text>
          </HStack>
        </Td>
        <Td>
          <Text textStyle="smMdSemi">{row?.phone ?? "-"}</Text>
        </Td>
        <Td style={{ maxWidth: "20vw" }}>
          <Text
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
            textStyle="caption"
          >
            {row?.description}
          </Text>
        </Td>
        <Td>
          <Text textStyle="smMdSemi">{"TBD"}</Text>
        </Td>
        <Td>
          <Text textStyle="smMdSemi">{"TBD"}</Text>
        </Td>
        <Td>
          <Text textStyle="smMdSemi">{"TBD"}</Text>
        </Td>
        <Td style={{ maxWidth: "20vw" }}>
          {row.userStatusInGroup === "pending" ? (
            <Button variant="outline" size="sm" isDisabled>
              Pending...
            </Button>
          ) : null}
          {row.userStatusInGroup === "accepted" ? (
            <Button variant="outline" size="sm" isDisabled>
              Joined
            </Button>
          ) : null}
          {row.userStatusInGroup === null ? (
            <Button
              variant="outline"
              size="sm"
              color="white"
              isLoading={isRequestingGroup}
              onClick={() => void requestToJoin(row.id)}
            >
              Request to join
            </Button>
          ) : null}
        </Td>
      </>
    );
  };

  const requestToJoin = (id: string) => {
    requestGroup(id);
  };

  return (
    <Box>
      {(myGroupsData?.docs?.length ?? 0) === 0 && !isMyGroupLoading && (
        <Text fontSize={18} fontWeight={700} color="white" mt={6}>
          Welcome to LnQ. To get started make sure to join at least one group.
        </Text>
      )}
      <StyledTable
        columns={tableColumns}
        customRowRenderers={groupRow}
        data={allGroupsData ?? []}
        loading={isGettingAllGroups}
      />
      <Pagination
        pages={groupsData?.totalPages ?? 0}
        currentPage={page}
        setPage={setPage}
      />
    </Box>
  );
};
